import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Service Providers, calea noua. Perechile vechi, din
 * `application/controllers/Services_providers.php`:
 *
 *   service_providers      -> GET  /services_providers          (cadrul)
 *   service_providers_list -> GET  /services_providers/list     (lista)
 *   create                 -> POST /services_providers
 *   addSettings            -> POST /services_providers/settings
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * ADRESA ECRANULUI nu e sub `settings/`, desi el e in zona Global a setarilor:
 * in `Services\Navigation\SettingsMenu` scrie `services_providers/service_providers`,
 * si aia e adresa si aici.
 */

/**
 * Un modul din `it_modules`, pentru lista cu bife a formularului. Eventee si
 * Easeetask nu vin deloc; `default` = bifat de la inceput.
 */
export type ProviderModule = {
  id: number
  name: string
  slug: string
  default?: boolean
}

export type ServiceProvidersShell = {
  /** Dreptul de scriere SI `extended_rights == 2`, hotarat pe server. */
  canCreate: boolean
  modules: ProviderModule[]
  chatTemplates: Record<string, string>
  licenseTypes: Record<string, string>
}

/** Un rand din lista, cu cheile pe care le scria modelul vechi. */
export type ServiceProviderRow = {
  ID: number
  Nume: string
}

export type ServiceProvidersPage = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: ServiceProviderRow[]
}

export type ProvidersTableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

/** Coloana SQL a fiecarui indice - `$columns` din `getActiveProvidersList()`. */
export const ORDER_NAMES = ['ID', 'Nume']

/** Aceiasi parametri pe care ii trimitea DataTables pe `service_providers_list`. */
export function providersTableParams(r: ProvidersTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function serviceProvidersShellQuery() {
  return queryOptions({
    queryKey: ['services_providers', 'shell'],
    queryFn: () => apiGet<ServiceProvidersShell>('/services_providers'),
  })
}

export function serviceProvidersTableQuery(r: ProvidersTableRequest) {
  return queryOptions({
    queryKey: ['services_providers', 'table', r],
    queryFn: () => apiGet<ServiceProvidersPage>(`/services_providers/list?${providersTableParams(r).toString()}`),
  })
}

/** Ce trimite formularul "New SP", camp cu camp ca in macheta veche. */
export type NewProvider = {
  company_name: string
  Nume: string
  offices: string
  firstname: string
  lastname: string
  email: string
  username: string
  password: string
  /** SLUG-urile modulelor bifate - vezi `DbalServiceProvidersSource`. */
  modules: string[]
  chat_template: string
  license_type: string
}

export type CreatedProvider = {
  service_provider: number
  company: number
}

/** `create`: furnizorul, firma lui si administratorul lui. */
export function createServiceProvider(form: NewProvider) {
  const fields: Record<string, PostValue> = {
    company_name: form.company_name,
    Nume: form.Nume,
    offices: form.offices,
    'user[firstname]': form.firstname,
    'user[lastname]': form.lastname,
    'user[email]': form.email,
    'user[username]': form.username,
    'user[password]': form.password,
  }

  return apiPost<CreatedProvider>('/services_providers', fields)
}

/**
 * `addSettings`: sabloanele copiate pe furnizorul tocmai facut.
 *
 * A doua cerere, ca in portal - vezi capul lui `ServiceProviders` (API) pentru
 * de ce n-au fost unite: al doilea pas e o suta de scrieri, si trebuie sa se
 * poata relua fara sa se faca inca un furnizor.
 */
export function addProviderSettings(created: CreatedProvider, form: NewProvider) {
  return apiPost<{ status: string; errors: string[] }>('/services_providers/settings', {
    service_provider: created.service_provider,
    company: created.company,
    modules: form.modules,
    chat_template: form.chat_template,
    license_type: form.license_type,
  })
}
